/**
 * The strings a screen builds out of several parts.
 *
 * Every function here takes the translator, or the language, or both. That is
 * the point of the file: a meta line like "Mathematics · School · 10 questions
 * · Assigned to 1 student · Updated 1 Sep" is four translated fragments and a
 * date, and assembling it inside each screen is how it ends up assembled four
 * slightly different ways.
 *
 * The separator is not translated. ` · ` is punctuation, it is the same in
 * both languages, and a key for it would be a key nobody could translate
 * wrongly but everybody would have to look up.
 *
 * ── Counts ───────────────────────────────────────────────────────────────
 *
 * There is no pluralization engine (V1.md section 18). Both V1 languages have
 * the same one/other split, so a count picks between two keys — `questionsOne`
 * and `questionsOther` — and `countOf` is the two-line function that does it.
 * A language with a dual or a paucal form would need more than this, and would
 * be the reason to add it.
 */
import {
  formatDate as formatDateIn,
  formatDateTime as formatDateTimeIn,
  formatDay,
  formatMoney,
  formatNumber,
  formatPercent,
  formatRelativeDay,
  formatShortDay,
  formatTime as formatTimeIn,
  type Money,
  type Translate,
} from '@athena/i18n'
import { useLanguage, useTranslate } from '@athena/ui'

import type { Language } from './language.js'

/**
 * Dates in the language of the page, not of the browser.
 *
 * The conventions themselves — "29 sept 2026" and "Sep 29, 2026", the locale
 * each language formats with, which dates carry a year — live in
 * `@athena/i18n`'s `format.ts`, so the administration and the marketing site
 * print a day the way this app does. These are the names this app's screens
 * and tests already use for them.
 */
export function formatDate(iso: string, language: Language): string {
  return formatDateIn(iso, language)
}

/** A day without its year — "29 sept", "Sep 29" — for a chart axis, where the year is implied. */
export function formatShortDate(iso: string, language: Language): string {
  return formatShortDay(iso, language)
}

/** The time of day, in the reader's clock: "3:42 PM", "3:42 p. m.". */
export function formatTime(iso: string, language: Language): string {
  return formatTimeIn(iso, language)
}

/**
 * A calendar day the server grouped by, in UTC.
 *
 * For values that name a UTC day rather than an instant — a daily summary is
 * stamped at midnight UTC. Formatting one in the reader's zone would put it on
 * the previous day for everyone west of Greenwich, so the label and the
 * attempts it counts would disagree.
 */
export function formatUtcDay(iso: string, language: Language): string {
  return formatDay(iso, language, { utc: true })
}

/** The same UTC day without its year, for a chart axis whose range already says which year. */
export function formatUtcShortDay(iso: string, language: Language): string {
  return formatShortDay(iso, language, { utc: true })
}

export function formatDateTime(iso: string, language: Language): string {
  return formatDateTimeIn(iso, language)
}

/**
 * "tomorrow", "in 3 days" — counted in calendar days, not in elapsed hours.
 *
 * 09:00 tomorrow is nineteen hours away at 14:00 today, which rounds to
 * "today" by elapsed time. That is the kind of wrong that costs a deadline.
 */
export function relativeDay(iso: string, language: Language, now: Date = new Date()): string {
  return formatRelativeDay(iso, language, now)
}

/** The countable things the product names. One `…One`/`…Other` pair each. */
export type Countable =
  | 'questions'
  | 'attempts'
  | 'students'
  | 'practices'
  | 'minutes'
  | 'seconds'
  | 'available'
  | 'matches'

export function countOf(t: Translate, thing: Countable, count: number): string {
  return count === 1 ? t(`count.${thing}One`) : t(`count.${thing}Other`, [count])
}

/**
 * A number of seconds, written out: "42 minutes 17 seconds".
 *
 * The sentence a screen reader is given for the countdown, which is why it is
 * words rather than the `42:17` the eye gets. Seconds alone under a minute —
 * "0 minutes 45 seconds" is worse to hear than "45 seconds", and the whole
 * point of the last minute is that it is short.
 *
 * Built from the same `…One`/`…Other` pairs every other count in the product
 * uses, so a language that says "1 minuto" says it here too.
 */
export function remainingWords(t: Translate, seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safe / 60)
  const rest = safe % 60

  // Seconds alone under a minute. "0 minutes 45 seconds" is worse to hear than
  // "45 seconds", and the whole point of the last minute is that it is short.
  if (minutes === 0) return countOf(t, 'seconds', rest)
  // The space is not translated, for the reason the ` · ` separator above is
  // not: both languages put the two counts in this order with a space between.
  return `${countOf(t, 'minutes', minutes)} ${countOf(t, 'seconds', rest)}`
}

/** The same, as the sentence the countdown carries for a screen reader. */
export function timeRemaining(t: Translate, seconds: number): string {
  return t('attempt.timer.remaining', [remainingWords(t, seconds)])
}

/**
 * A duration in minutes, as a tutor set it and as a student is told it.
 *
 * One function so the number on the tutor's settings panel and the number in
 * the warning before a student starts are the same words.
 */
export function duration(t: Translate, minutes: number): string {
  return countOf(t, 'minutes', minutes)
}

/**
 * A price, from the integer minor units the API carries: "US$15", "$15".
 *
 * The rule — which symbol, when cents show — is `formatMoney` in
 * `@athena/i18n`, shared with the marketing site's pricing, so a plan costs
 * the same number of characters on both.
 */
export function money(amount: Money, language: Language): string {
  return formatMoney(amount, language)
}

const SEPARATOR = ' · '

export function categoryLine(practice: { subject: { name: string } }): string {
  // The name arrives localized from the API, out of the subject's translation
  // rows. Nothing here looks it up, which is what stops a hard-coded English
  // list drifting from the catalog.
  return practice.subject.name
}

export function accessSummary(
  t: Translate,
  language: Language,
  practice: {
    archived: boolean
    archivedAt: string | null
    availableUntil: string | null
    maxAttempts: number | null
    /**
     * The published version's time limit, when the practice has one. Optional
     * because the summary predates the timer and several callers pass a
     * practice row that has no version on it.
     */
    publishedVersion?: { timed: boolean; durationMinutes: number | null } | null
  },
): string {
  const parts = [
    practice.archived
      ? practice.archivedAt
        ? t('practice.meta.archivedOn', [formatDate(practice.archivedAt, language)])
        : t('practice.meta.archived')
      : t('practice.meta.active'),
    practice.availableUntil === null
      ? t('practice.meta.noEndDate')
      : t('practice.meta.availableUntil', [formatDateTime(practice.availableUntil, language)]),
    practice.maxAttempts === null
      ? t('practice.meta.unlimitedAttempts')
      : practice.maxAttempts === 1
        ? t('practice.meta.attemptsEachOne')
        : t('practice.meta.attemptsEachOther', [practice.maxAttempts]),
    // The time limit reads off the *published* version, because that is the
    // one a student sitting the practice right now is under. A draft with a
    // different limit is shown on the settings panel, where it is labelled as
    // unpublished.
    practice.publishedVersion?.timed && practice.publishedVersion.durationMinutes !== null
      ? t('practice.meta.timed', [duration(t, practice.publishedVersion.durationMinutes)])
      : t('practice.meta.untimed'),
  ]
  return parts.join(SEPARATOR)
}

export function practiceMeta(
  t: Translate,
  language: Language,
  input: {
    questionCount: number | null
    assignedCount: number
    updatedAt: string
  },
): string {
  // The subject heading already files every row. Repeating its name here
  // would add the same words to every item and slow down scanning the facts
  // that actually differ between practices.
  const parts = [
    input.questionCount === null
      ? t('practice.meta.notPublishedYet')
      : countOf(t, 'questions', input.questionCount),
    input.assignedCount === 0
      ? t('practice.meta.notAssigned')
      : input.assignedCount === 1
        ? t('practice.meta.assignedToOne')
        : t('practice.meta.assignedToOther', [input.assignedCount]),
    t('practice.meta.updated', [formatDate(input.updatedAt, language)]),
  ]
  return parts.join(SEPARATOR)
}

/**
 * The same formatters, bound to the page's language and translator.
 *
 * What components use. The free functions above stay exported because they are
 * where the rules are, and a rule is easier to test as a function than through
 * a rendered component.
 */
export function useFormat() {
  const t = useTranslate()
  const language = useLanguage()

  return {
    date: (iso: string) => formatDate(iso, language),
    shortDate: (iso: string) => formatShortDate(iso, language),
    dateTime: (iso: string) => formatDateTime(iso, language),
    time: (iso: string) => formatTime(iso, language),
    utcDay: (iso: string) => formatUtcDay(iso, language),
    utcShortDay: (iso: string) => formatUtcShortDay(iso, language),
    relativeDay: (iso: string, now?: Date) => relativeDay(iso, language, now),
    count: (thing: Countable, count: number) => countOf(t, thing, count),
    duration: (minutes: number) => duration(t, minutes),
    money: (amount: Money) => money(amount, language),
    number: (value: number) => formatNumber(value, language),
    percent: (percentage: number) => formatPercent(percentage, language),
    timeRemaining: (seconds: number) => timeRemaining(t, seconds),
    remainingWords: (seconds: number) => remainingWords(t, seconds),
    categoryLine,
    accessSummary: (practice: Parameters<typeof accessSummary>[2]) =>
      accessSummary(t, language, practice),
    practiceMeta: (input: Parameters<typeof practiceMeta>[2]) => practiceMeta(t, language, input),
  }
}
